/**
 * The tools the report-bundle assistant works with. The server hands these to
 * the model with every turn; the studio runs them, against the files in its
 * editor and the run in its preview, and sends back what each returned.
 *
 * Files are edited through the Messages API's own text editor tool, the schema
 * the model is trained on; the rest are this app's: compile, preview, the run,
 * and the SDK's source to read.
 */

export const ASSISTANT_EDITOR_TOOL_NAME = 'str_replace_based_edit_tool';

export type AssistantToolName =
  | typeof ASSISTANT_EDITOR_TOOL_NAME
  | 'delete_file'
  | 'compile'
  | 'preview'
  | 'inspect_run'
  | 'load_run'
  | 'read_reference'
  | 'save_schema';

interface CustomTool {
  name: Exclude<AssistantToolName, typeof ASSISTANT_EDITOR_TOOL_NAME>;
  description: string;
  input_schema: { type: 'object'; properties: Record<string, unknown>; required?: string[] };
}

export const ASSISTANT_EDITOR_TOOL = { type: 'text_editor_20250728', name: ASSISTANT_EDITOR_TOOL_NAME } as const;

export const ASSISTANT_CUSTOM_TOOLS: CustomTool[] = [
  {
    name: 'delete_file',
    description: 'Delete one of the bundle\'s files. definition.ts and document.tsx are the entries and cannot be deleted.',
    input_schema: { type: 'object', properties: { path: { type: 'string', description: 'the file, e.g. helpers.ts' } }, required: ['path'] },
  },
  {
    name: 'compile',
    description: 'Save the working files as the bundle\'s draft and compile them, as the author\'s Save does. Returns whether the draft compiles, every problem as file:line:column and message, and each fixture\'s result. Call it after a set of edits and before preview: the preview only runs what last compiled. Saving never publishes; publishing a version is the author\'s.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'preview',
    description: 'Run the last compiled draft against the run in the studio\'s preview, as the Preview tab draws it, and return what it shows as text: the verdict and its label, the findings and labels, any point file the report asks for that the preview context does not hold, and an outline of the document (headings, paragraphs, tables row by row, figure captions). Whatever a block prints because a prop is unset or it found nothing is marked [unset]. `section` narrows the outline to the sections whose heading contains that text. Refused when the files changed since the last compile.',
    input_schema: { type: 'object', properties: { section: { type: 'string', description: 'only the sections whose heading contains this text' } } },
  },
  {
    name: 'inspect_run',
    description: 'Describe the run the preview reads (a real run once one is loaded, else the sample): its procedure, DUT and equipment, the procedure\'s parameters (lists row by row; choices with what the run selected and what else was offered), and its steps with the identifiers and values of their readings. With no input, an overview. `step` (a step number from the overview, or text in its title) gives one step whole: every reading\'s identifier, values, records, files and choices, and the step\'s checks. `search` lists every parameter, step and reading whose identifier, title or value contains the text. Bind blocks only to identifiers you have seen here.',
    input_schema: {
      type: 'object',
      properties: {
        step: { type: 'string', description: 'a step number from the overview, or text in a step title' },
        search: { type: 'string', description: 'text to find in identifiers, titles and values' },
      },
    },
  },
  {
    name: 'load_run',
    description: 'Load a run, by its id, as the preview\'s context, read the way this draft reads it: the point files its traces ask for and its own settings. The author sees the preview change to it. Use it when the author names a run to check the report against.',
    input_schema: { type: 'object', properties: { run_id: { type: 'string' } }, required: ['run_id'] },
  },
  {
    name: 'read_reference',
    description: 'Read, never edit, the report SDK\'s source (the code behind every block, prop and helper a bundle imports from @veriform/report-sdk, under sdk/) and the starter bundles a new bundle is made from (under examples/). With no input, lists the files. `path` reads one, numbered by line; `offset` (the first line, from 1) and `limit` (how many lines) read part of it. `search` is a regular expression; every matching line across the files comes back as path:line: text.',
    input_schema: {
      type: 'object',
      properties: {
        path: { type: 'string' },
        offset: { type: 'integer', minimum: 1 },
        limit: { type: 'integer', minimum: 1 },
        search: { type: 'string', description: 'a regular expression, matched case-insensitively' },
      },
    },
  },
];

export const ASSISTANT_TOOLS = [ASSISTANT_EDITOR_TOOL, ...ASSISTANT_CUSTOM_TOOLS];

/**
 * The tools of the assistant on a schema's own page: one file, inputs.json,
 * edited with the same editor tool and saved as the schema's draft by
 * save_schema, as the author's Save does; and the SDK's source to read.
 */
export const SCHEMA_ASSISTANT_CUSTOM_TOOLS: CustomTool[] = [
  {
    name: 'save_schema',
    description: 'Save inputs.json as the schema\'s draft, as the author\'s Save does, and check it. Returns whether it is a sheet and every problem with it, how many inputs it holds, what it changes since the latest published version (ids renamed or removed break records entered on that version and every report that reads them by id), and the reports that read the schema. Saving never publishes; publishing a version is the author\'s.',
    input_schema: { type: 'object', properties: {} },
  },
  ASSISTANT_CUSTOM_TOOLS.find((t) => t.name === 'read_reference') as CustomTool,
];

export const SCHEMA_ASSISTANT_TOOLS = [ASSISTANT_EDITOR_TOOL, ...SCHEMA_ASSISTANT_CUSTOM_TOOLS];
